import { useEffect } from "react";
import {
  LEGALIX_SITE_URL,
  LEGALIX_SUPPORT_EMAIL,
  LEGALIX_SUPPORT_PHONE,
} from "@/components/InsufficientCreditsDialog";
import { trackWallCta, trackWallShown } from "@/lib/mixpanel";

export type FeaturePreviewAppId =
  | "recordings"
  | "ai-assistant"
  | "word-addon"
  | "scan-documents-extraction";

type FeatureCopy = {
  title: string;
  body: string;
  motivation: string;
};

const FEATURE_COPY: Record<FeaturePreviewAppId, FeatureCopy> = {
  recordings: {
    title: "הקלטת שיחות",
    body: "מקליטים פגישת לקוח או ישיבה, מקבלים תמלול אוטומטי וסיכום משפטי, ואז חוזרים לרגעים חשובים בלחיצה.",
    motivation:
      "מועיל במיוחד אחרי פגישת ייעוץ, דיון הוכחות או שיחת טלפון ארוכה — בלי לרשום הכל ידנית ובלי לפספס התחייבויות. חוסך שעות של סיכום ומפחית טעויות לפני ניסוח מכתב או כתב בי־דין.",
  },
  "ai-assistant": {
    title: "סוכן משפטי",
    body: "שואלים שאלה בעברית ומקבלים תשובה מבוססת ידע — לבירור ראשוני, ניסוח והכוונה לפני עבודה מעמיקה בתיק.",
    motivation:
      "מתאים כשצריך לבדוק במהירות סעיף, להבין כיוון משפטי או לנסח טיוטה ראשונה למכתב התראה. במקום לחפש שעות במקורות — מקבלים נקודת פתיחה חזקה לדבר עם הלקוח או להמשיך בניסוח.",
  },
  "word-addon": {
    title: "תוסף Word",
    body: "עובדים על המסמך בתוך Word עם כלי Legalix — ניסוח, עריכה וסיוע משפטי בזמן כתיבה במסמך עצמו.",
    motivation:
      "שימושי כשמכינים חוזה, תצהיר או כתב תביעה ורוצים עזרה ממוקדת בלי לעבור בין חלונות. מקצר סבבי עריכה ומאפשר להגיע לטיוטה מוכנה מהר יותר.",
  },
  "scan-documents-extraction": {
    title: "מסמכים (OCR)",
    body: "מעלים PDF או סריקה ומחלצים טקסט מובנה שאפשר לערוך, להעתיק ולחפש — במקום מסמך «תמונה» סגור.",
    motivation:
      "חיוני לחוזים סרוקים, טפסים מבית משפט ומסמכי לקוח שהגיעו בוואטסאפ. הופך נייר דיגיטלי לטקסט שמיש לדוא״ל, לניתוח ולחיפוש בתיק.",
  },
};

const PLAN_NOTE =
  "בתוכנית הנוכחית אין קרדיטים לשימוש בפיצ׳ר הזה. כדי להפעיל אותו צריך תוכנית מתאימה או הרחבת קרדיטים.";

type FeaturePreviewProps = {
  appId: FeaturePreviewAppId;
};

export const FeaturePreview = ({ appId }: FeaturePreviewProps) => {
  const copy = FEATURE_COPY[appId];

  useEffect(() => {
    trackWallShown({
      wall: "feature_preview",
      app: appId,
      surface: "web",
      ui: "feature_preview",
      feature: appId,
    });
  }, [appId]);

  const handleCta = (cta: "site" | "phone" | "email") => {
    trackWallCta({ cta, wall: "feature_preview", surface: "web", ui: "feature_preview" });
  };

  return (
    <section
      className="mx-auto flex h-full max-w-xl flex-col justify-center gap-4 p-8 text-right"
      dir="rtl"
      aria-label={copy.title}
    >
      <h1 className="text-2xl font-semibold text-indigo-900">{copy.title}</h1>
      <p className="text-base text-slate-600 leading-relaxed">{copy.body}</p>
      <p className="text-base text-slate-700 leading-relaxed">{copy.motivation}</p>
      <p className="text-sm text-slate-500 leading-relaxed">{PLAN_NOTE}</p>
      <div className="mt-2 space-y-1 text-sm text-slate-700">
        <p>
          אתר:{" "}
          <a
            href={LEGALIX_SITE_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="underline font-medium"
            onClick={() => handleCta("site")}
          >
            {LEGALIX_SITE_URL}
          </a>
        </p>
        <p>Legalix</p>
        <p>
          <a
            href="tel:+972552136446"
            className="underline"
            dir="ltr"
            onClick={() => handleCta("phone")}
          >
            {LEGALIX_SUPPORT_PHONE}
          </a>
        </p>
        <p>
          email{" "}
          <a
            href={`mailto:${LEGALIX_SUPPORT_EMAIL}`}
            className="underline"
            dir="ltr"
            onClick={() => handleCta("email")}
          >
            {LEGALIX_SUPPORT_EMAIL}
          </a>
        </p>
      </div>
    </section>
  );
};

export default FeaturePreview;
